import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {
  FONT_PROXIMA_14,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import ClickOutWrapper from 'ui-library/ClickOutWrapper';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import TextInput from 'ui-library/inputs/TextInput';
import {callOnEnter} from 'util/Keyboard';
import {InputChangeEvent, TextAreaChangeEvent} from 'util/Types';

export interface ClickToEditTextFieldProps {
  value: string;
  sanitize?: (val: string) => string;
  onSaveValue: (val: string) => void;
  placeholder?: string;
  spellCheck?: boolean;
  editable?: boolean;
  isArea?: boolean;
  disableResize?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * Display a value with a click-to-edit mode.
 *
 * The field is editable after clicking into it. Once the user clicks away, the value is saved.
 *
 * @param value Value to display.
 * @param sanitize Callback to sanitize input. Run on every change the input.
 * @param onSaveValue Callback to save an updated value.
 * @param placeholder Placeholder to display if the value is falsy.
 * @param spellCheck Value for the spellcheck attribute on the text input element.
 * @param editable Whether the field can enter editing mode.
 * @param isArea Determines if a textarea or a text input is used for editing mode.
 * @param disableResize Disable the ability to resize if the component is a textarea.
 * @param dataTestAttrs Data test attributes to apply to the component.
 * @constructor
 */
export default function ClickToEditTextField({
  value,
  sanitize,
  onSaveValue,
  placeholder,
  spellCheck,
  editable = true,
  isArea = false,
  disableResize = true,
  dataTestAttrs,
}: ClickToEditTextFieldProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState<string | undefined>();
  const TextEditComponent = isArea ? 'textarea' : TextInput;
  const saveValue = (): void => {
    setEditing(false);
    if (tempValue !== undefined) {
      onSaveValue(tempValue);
    }
    setTempValue(undefined);
  };

  if (!editing) {
    const textStyles = [styles.displayText];
    if (editable) {
      textStyles.push(styles.clickableText);
      textStyles.push(value === '' ? styles.placeholderFont : styles.valueFont);
    } else {
      textStyles.push(styles.valueFont);
    }
    return (
      <div
        className={css(...textStyles)}
        onClick={() => {
          if (!editable) {
            return;
          }
          setTempValue(value);
          setEditing(true);
        }}
        {...getTestProps(dataTestAttrs)}
      >
        {value || (editable ? placeholder : '')}
      </div>
    );
  } else {
    return (
      <ClickOutWrapper onClickOut={saveValue}>
        <TextEditComponent
          value={tempValue}
          onKeyPress={callOnEnter(saveValue)}
          onChange={(e: InputChangeEvent | TextAreaChangeEvent) => {
            if (sanitize != null) {
              const sanitizedInput = sanitize(e.target.value);
              setTempValue(sanitizedInput);
            } else {
              setTempValue(e.target.value);
            }
          }}
          className={css(styles.valueFont, disableResize && styles.disableResize)}
          autoFocus={true}
          spellCheck={spellCheck}
          placeholder={placeholder}
          {...getTestProps(dataTestAttrs)}
        />
      </ClickOutWrapper>
    );
  }
}

const styles = StyleSheet.create({
  disableResize: {
    resize: 'none',
  },
  displayText: {
    width: '100%',
    overflowWrap: 'break-word',
  },
  clickableText: {
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
      cursor: 'pointer',
    },
  },
  valueFont: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  placeholderFont: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
});
